<template>
    <div class="marketplace-canvas">
      <div class="container py-3 py-md-4">
        
        <!-- 🔍 Header Component: Search Bar & Filter Controls -->
        <header class="search-section-wrapper mb-4">
          <div class="d-flex gap-2 align-items-center">
            <div class="search-input-group flex-grow-1 position-relative">
              <span class="search-icon-inside position-absolute">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </span>
              <input 
                type="text" 
                v-model="searchQuery"
                placeholder="Search products, brands, models" 
                class="form-control top-search-input"
              />
            </div>
            
            <!-- Filter Matrix Action Trigger -->
            <button class="filter-panel-trigger-btn" aria-label="Open filter parameter drawer">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line>
                <line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line>
                <line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line>
                <line x1="17" y1="16" x2="23" y2="16"></line>
              </svg>
            </button>
          </div>
  
          <!-- 🏷️ Horizontal Smooth Scrolling Category Rail -->
          <nav class="category-scroll-rail mt-3 d-flex gap-2" aria-label="Product Categories">
            <button 
              v-for="cat in categories" 
              :key="cat"
              class="category-chip-btn text-nowrap"
              :class="{ 'is-active': activeCategory === cat }"
              @click="activeCategory = cat"
            >
              {{ cat }}
            </button>
          </nav>
        </header>
  
        <!-- 📦 Core Dynamic Layout Grid (Fluid Mobile-to-Desktop Matrix) -->
        <main>
            <!-- row-cols-2 for two rows on mobile -->
          <div class="row row-cols-md-3 row-cols-lg-4 g-3 g-md-4">
            <div class="col mb-4" v-for="product in filteredProducts" :key="product.id">
              <article class="product-listing-card h-100 position-relative">
                
                <!-- Floating Wishlist Favorite Action Trigger Toggle Button -->
                <button 
                  class="wishlist-float-toggle-btn position-absolute"
                  :class="{ 'is-favorited': product.isWishlisted }"
                  @click="toggleProductWishlist(product.id)"
                  :aria-label="product.isWishlisted ? 'Remove item from wishlist' : 'Add item to wishlist'"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" :fill="product.isWishlisted ? '#183883' : 'none'" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                </button>
  
                <!-- Image Canvas Area -->
                <div class="card-image-canvas">
                  <img :src="product.imageUrl" :alt="product.title" class="img-fluid listing-hero-img" />
                </div>
  
                <!-- Content Descriptive Meta Block -->
                <div class="card-body-meta-box d-flex flex-column pt-2">
                  <!-- Verified Badge Subtext Indicator -->
                  <div class="merchant-verification-row d-flex align-items-center gap-1 mb-1">
                    <span class="verify-badge-icon d-flex align-items-center justify-content-center">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span class="merchant-name-text">{{ product.merchantName }}</span>
                  </div>
  
                  <!-- Product Name Title Header -->
                  <h2 class="product-title-heading mb-1">{{ product.title }}</h2>
  
                  <!-- Rating Review Metric Layout Group -->
                  <div class="rating-metrics-row d-flex align-items-center gap-1 mb-2">
                    <span class="star-icon-fill">★</span>
                    <span class="rating-score-text">{{ product.ratingScore }}</span>
                    <span class="review-count-total">({{ product.reviewCount }})</span>
                  </div>
  
                  <!-- Financial Pricing Node Stack -->
                  <div class="product-amount-container mt-auto">
                    <div class="main-price-currency mb-1">
                      ₦{{ formatPrice(product.priceInNaira) }}
                    </div>
                    <div class="financing-installment-badge">
                      From ₦{{ formatPrice(product.monthlyInstallment) }}/mo
                    </div>
                  </div>
  
                </div>
              </article>
            </div>
          </div>
  
          <!-- 📭 Empty State Dynamic Fallback Component Binding -->
          <div v-if="filteredProducts.length === 0" class="py-5 text-center text-muted">
            <p class="font-weight-bold mb-1">No products found matching filters</p>
            <small>Refine your search parameter keywords or adjust the selection filter chips rail above.</small>
          </div>
        </main>
  
      </div>
    </div>
  </template>
  
  <script>
  // @ts-nocheck
  import Vue from 'vue';
  
  export default Vue.extend({
    name: 'ProductList',
    data() {
      return {
        searchQuery: '',
        activeCategory: 'All Items',
        categories: ['All Items', 'Electronics', 'Air Conditioning', 'Appliances', 'Mobile Devices'],
        
        // Seeded Mock Data Structure mapping your design image specifications identically
        products: [
          {
            id: 1,
            merchantName: 'Konga Direct',
            title: 'Product image',
            imageUrl: require('../../assets/images/prop1.svg'), // '../../assets/images/prop1.svg',
            ratingScore: 4.7,
            reviewCount: 128,
            priceInNaira: 180000,
            monthlyInstallment: 15000,
            categoryType: 'Electronics',
            isWishlisted: false
          },
          {
            id: 2,
            merchantName: 'Fouani Store',
            title: 'Royal 1.5HP Inverter Split AC',
            imageUrl: require('../../assets/images/prop1.svg'),
            ratingScore: 4.8,
            reviewCount: 94,
            priceInNaira: 320000,
            monthlyInstallment: 28000,
            categoryType: 'Air Conditioning',
            isWishlisted: false
          },
          {
            id: 3,
            merchantName: 'Pro-Gen Outlets',
            title: 'Pro-Gen 5500i Heavy Duty Generator',
            imageUrl: require('../../assets/images/prop1.svg'),
            ratingScore: 4.5,
            reviewCount: 62,
            priceInNaira: 450000,
            monthlyInstallment: 39000,
            categoryType: 'Electronics',
            isWishlisted: false
          },
          {
            id: 4,
            merchantName: 'Luxury Bedding Co.',
            title: 'Royal Orthopedic Double Layer Mattress',
            imageUrl: require('../../assets/images/prop1.svg'),
            ratingScore: 4.9,
            reviewCount: 115,
            priceInNaira: 240000,
            monthlyInstallment: 21000,
            categoryType: 'Appliances',
            isWishlisted: false
          }
        ]
      };
    },
    computed: {
      // Dynamic real-time combined computed list filter pipe
      filteredProducts() {
        return this.products.filter(product => {
          const matchesCategory = this.activeCategory === 'All Items' || product.categoryType === this.activeCategory;
          const matchesSearch = product.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                                product.merchantName.toLowerCase().includes(this.searchQuery.toLowerCase());
          return matchesCategory && matchesSearch;
        });
      }
    },
    methods: {
      formatPrice(val) {
        return val.toLocaleString('en-NG');
      },
      toggleProductWishlist(productId) {
        const matchIndex = this.products.findIndex(p => p.id === productId);
        if (matchIndex > -1) {
          this.products[matchIndex].isWishlisted = !this.products[matchIndex].isWishlisted;
        }
      }
    }
  });
  </script>
  
<style lang="scss" scoped>
$font-sans: 'Public Sans', system-ui, -apple-system, sans-serif;
$color-primary: #183883;
$color-bg-base: #F8FAFC;
$color-border: #e2e8f0;
$color-text-main: #475569;
$color-text-muted: #64748b;

.marketplace-canvas {
  font-family: $font-sans;
  background-color: #fafbfc;
  min-height: 100vh;
  color: #0f172a;
}

/* ─── 🔍 HEADER CONTROLS AND HORIZONTAL SCROLL CHIPS ─── */
.search-input-group {
  .search-icon-inside {
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $color-text-muted;
    pointer-events: none;
    display: flex;
    align-items: center;
  }

  .top-search-input {
    height: 48px;
    padding-left: 44px;
    background-color: #ffffff;
    border: 1px solid $color-border;
    border-radius: 10px;
    font-size: 0.95rem;
    color: #0f172a;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: $color-primary;
      box-shadow: 0 0 0 3px rgba(24, 56, 131, 0.05);
    }
    
    &::placeholder {
      color: $color-text-muted;
      opacity: 0.8;
    }
  }
}

.filter-panel-trigger-btn {
  width: 48px;
  height: 48px;
  background-color: #dbeafe; // Accurate blue fallback tint matching image design
  color: $color-primary;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(24, 56, 131, 0.15);
  }
}

.category-scroll-rail {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; // Hides default scrollbar tracks cleanly on Firefox
  
  &::-webkit-scrollbar {
    display: none; // Hides default scrollbar tracks on Chrome/Safari viewports
  }

  .category-chip-btn {
    padding: 8px 18px;
    font-size: 0.875rem;
    font-weight: 600;
    color: $color-text-main;
    background-color: #f1f5f9;
    border: 1px solid transparent;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(.is-active) {
      background-color: #e2e8f0;
    }

    &.is-active {
      background-color: $color-primary; // Accurate deep blue focus token highlight
      color: #ffffff;
    }
  }
}

/* ─── 📦 ECOMMERCE LISTING COMPONENT CARDS ─── */
.product-listing-card {
  background-color: #ffffff;
  border: 1px solid $color-border;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.04);
  }
}

.card-image-canvas {
  width: 100%;
  aspect-ratio: 1 / 1; // Enforces uniform grid boxes, preventing alignment distortion
  background-color: $color-bg-base;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .listing-hero-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.wishlist-float-toggle-btn {
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  background-color: rgba(255, 255, 255, 0.9);
  color: $color-text-main;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &.is-favorited {
    color: $color-primary;
    background-color: #ffffff;
  }
}

.card-body-meta-box {
  padding: 12px 14px 16px 14px;
  flex-grow: 1;
}

.merchant-verification-row {
  .verify-badge-icon {
    width: 14px;
    height: 14px;
    background-color: #dcfce7;
    border-radius: 50%;
  }

  .merchant-name-text {
    font-size: 0.75rem;
    font-weight: 700;
    color: $color-text-muted;
  }
}

.product-title-heading {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  text-align: left;
//   font-family: Lato;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2; // Clamps lines safely to protect row layouts
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.7em;
}

.rating-metrics-row {
  .star-icon-fill {
    color: #2563eb; // High contrast blue rating star color parameter matching original image
    font-size: 0.85rem;
  }

  .rating-score-text {
    font-size: 0.8rem;
    font-weight: 700;
    color: #0f172a;
  }

  .review-count-total {
    font-size: 0.8rem;
    color: $color-text-muted;
  }
}

.product-amount-container {
  .main-price-currency {
    font-size: 1.35rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.01em;
    text-align: left;
  }

  .financing-installment-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 700;
    color: $color-primary;
    background-color: #eff6ff; // Soft highlight indicator token row
    padding: 4px 8px;
    border-radius: 6px;
    width: 100%;
    text-align: left;
    box-sizing: border-box;
  }
}

/* ─── 📱 RESPONSIVE MEDIA BREAKPOINTS DEVIATIONS (No Mixins) ─── */
@media (min-width: 640px) {
  .card-body-meta-box {
    padding: 16px;
  }

  .product-title-heading {
    font-size: 1.05rem;
  }

  .product-amount-container {
    .main-price-currency {
      font-size: 1.5rem;
    }
  }
}
</style>

  